import type {
  CreatePartnerGroupRequest,
  PartnerGroupResponse,
  UpdatePartnerGroupRequest,
} from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * MD-01 field 4's list — partner groups. One key; the partner screens read
 * the same list for their picker and filter, so a rename shows everywhere at
 * once (the record is the id, never the text).
 */
export const PARTNER_GROUPS_KEY = ['partner-groups'] as const;

export function usePartnerGroups(includeInactive = false) {
  return useQuery({
    queryKey: [...PARTNER_GROUPS_KEY, { includeInactive }],
    queryFn: async (): Promise<PartnerGroupResponse[]> => {
      const response = await api.partnerGroups.list({
        query: includeInactive ? { includeInactive: 'true' as const } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.groups;
    },
  });
}

export function usePartnerGroupMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: PARTNER_GROUPS_KEY });
  const create = useMutation({
    mutationFn: async (body: CreatePartnerGroupRequest): Promise<PartnerGroupResponse> => {
      const response = await api.partnerGroups.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ groupId, body }: { groupId: string; body: UpdatePartnerGroupRequest }) => {
      const response = await api.partnerGroups.update({ params: { groupId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({ groupId, active }: { groupId: string; active: boolean }) => {
      const response = active
        ? await api.partnerGroups.reactivate({ params: { groupId }, body: {} })
        : await api.partnerGroups.deactivate({ params: { groupId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async ({ groupId }: { groupId: string }) => {
      const response = await api.partnerGroups.remove({ params: { groupId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive, remove };
}
